import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Badge, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const LEVEL_COLORS = {
  A1: 'text-yellow-700 bg-gold-100 border border-gold-300',
  A2: 'text-brand-600 bg-brand-50 border border-brand-200',
  B1: 'text-blue-700 bg-blue-50 border border-blue-200',
  B2: 'text-purple-700 bg-purple-50 border border-purple-200',
};

export default function StudentNotesPage() {
  const { addToast } = useToast();
  const [notes, setNotes]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [sidebarOpen, setSidebar] = useState(true);

  useEffect(() => {
    API.get('/notes')
      .then(({ data }) => {
        setNotes(data.notes || []);
        if (data.notes?.length) setSelected(data.notes[0]);
      })
      .catch(() => addToast('Failed to load notes', 'error'))
      .finally(() => setLoading(false));
  }, []);

  // Group notes by level
  const grouped = notes.reduce((acc, n) => {
    if (!acc[n.level]) acc[n.level] = [];
    acc[n.level].push(n);
    return acc;
  }, {});

  if (loading) return (
    <div className="flex items-center justify-center py-24"><Spinner size="lg" className="text-brand-500" /></div>
  );

  if (notes.length === 0) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <p className="text-5xl mb-4">📓</p>
      <h2 className="text-xl font-bold text-de-black mb-2">No notes yet</h2>
      <p className="text-gray-500">Your teacher hasn't added any study notes yet. Check back soon!</p>
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden" style={{ background:'#fff' }}>

      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-72' : 'w-0'} flex-shrink-0 transition-all duration-300 overflow-hidden border-r border-gray-200 bg-surface flex flex-col`}>
        <div className="p-4 border-b border-gray-200">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Study Notes</p>
        </div>
        <div className="overflow-y-auto flex-1 py-2">
          {['A1','A2','B1','B2'].map(level => {
            const lvlNotes = grouped[level];
            if (!lvlNotes?.length) return null;
            return (
              <div key={level} className="mb-4">
                <p className="px-4 py-1 text-xs font-bold text-gray-400 uppercase tracking-wider">{level}</p>
                {lvlNotes.map(n => (
                  <button key={n.id} onClick={() => setSelected(n)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-gray-100
                      ${selected?.id === n.id ? 'bg-white border-l-2 border-brand-500 font-semibold text-de-black' : 'text-gray-700'}`}>
                    <p className="truncate font-medium">{n.title}</p>
                    {n.topic && <p className="text-xs text-gray-400 truncate mt-0.5">{n.topic}</p>}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Note content */}
      <div className="flex-1 overflow-y-auto">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3">
          <button onClick={() => setSidebar(p => !p)}
            className="text-gray-400 hover:text-de-black transition-colors text-lg leading-none">
            ☰
          </button>
          {selected && (
            <>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${LEVEL_COLORS[selected.level]}`}>
                {selected.level}
              </span>
              {selected.topic && <span className="text-xs text-gray-500">{selected.topic}</span>}
            </>
          )}
        </div>

        {selected ? (
          <div className="max-w-3xl mx-auto px-8 py-10">
            <h1 className="text-3xl font-bold text-de-black mb-2 font-display">{selected.title}</h1>
            <p className="text-xs text-gray-400 mb-8">
              Last updated {new Date(selected.updated_at || selected.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })}
            </p>
            {/* Render rich HTML content from Quill */}
            <div
              className="prose prose-sm max-w-none text-de-text leading-relaxed"
              style={{ fontFamily:'inherit' }}
              dangerouslySetInnerHTML={{ __html: selected.content }}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-400 text-sm">Select a note from the sidebar to read it.</p>
          </div>
        )}
      </div>
    </div>
  );
}
